import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Helmet } from 'react-helmet';

import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';

import ThemeProviderV2 from '../ThemeProviderV2';
import Container from '../primitives/Container';
import TopNav from '../primitives/TopNav';
import FooterV2 from '../primitives/FooterV2';
import PrimaryButton from '../primitives/PrimaryButton';

import heroImg from '../../../assets/Banner_askneo-02.png';
import featureTextingImg from '../../../assets/lovable-feature-texting.jpg';
import featureCallingImg from '../../../assets/lovable-feature-calling.jpg';
import featureContactsImg from '../../../assets/lovable-feature-contacts.jpg';

const signupCode = process.env.REACT_APP_STRIPE_SIGNUP_CODE;
const signupTo = signupCode ? `/signup?code=${encodeURIComponent(signupCode)}` : '/signup';

const stats = [
  { value: '250x', label: 'Average ROI' },
  { value: '4.9★', label: 'Product Hunt' },
  { value: '4.8★', label: 'G2 Rating' },
  { value: '2 min', label: 'Setup Time' }
];

const featureRows = [
  {
    Icon: ForumOutlinedIcon,
    title: 'Two-Way Texting',
    body: 'Send and receive SMS & MMS in real time. Customers reply directly — no apps to download, no friction.',
    image: featureTextingImg,
    imageAlt: 'Two-way SMS texting interface showing customer conversation',
    reverse: false
  },
  {
    Icon: PhoneOutlinedIcon,
    title: 'Built-In Calling',
    body: 'Make and receive calls from your business number. Voicemail transcripts and call forwarding included.',
    image: featureCallingImg,
    imageAlt: 'VoIP calling dashboard with dialpad and voicemail transcripts',
    reverse: true
  },
  {
    Icon: PeopleOutlineIcon,
    title: 'Shared Team Inbox',
    body: 'One inbox for your whole team. Assign conversations, tag contacts, and never let a customer slip through the cracks.',
    image: featureContactsImg,
    imageAlt: 'Contact management dashboard with tags and team assignments',
    reverse: false
  }
];

const supportingFeatures = [
  {
    Icon: SmartToyOutlinedIcon,
    title: 'Customizable Chatbot',
    body: 'Automate responses with a customizable SMS chatbot'
  },
  {
    Icon: LocalOfferOutlinedIcon,
    title: 'Tags',
    body: 'Organize contacts with unlimited custom tags'
  },
  {
    Icon: CampaignOutlinedIcon,
    title: 'Broadcast SMS',
    body: 'Send targeted campaigns that land as 1:1 conversations'
  }
];

const setupSteps = [
  {
    number: '01',
    title: 'Pick your number',
    body: 'Choose a local business number, or port your existing one.'
  },
  {
    number: '02',
    title: 'Add your team',
    body: 'Invite teammates and assign roles — everyone shares one inbox.'
  },
  {
    number: '03',
    title: 'Start conversations',
    body: "Text and call customers. It's that simple."
  }
];

const testimonials = [
  {
    quote: 'AskNeo changed how we communicate with clients. We\'ve seen a massive increase in response rates and closed deals.',
    authorName: 'Karly M.',
    authorTitle: 'Sales Director'
  },
  {
    quote: 'Our team went from juggling 5 different tools to just AskNeo. The shared inbox is a game-changer for customer support.',
    authorName: 'Avi S.',
    authorTitle: 'Customer Success Lead'
  }
];

const Stars = () => (
  <Box sx={{ display: 'inline-flex', gap: 0.25 }} aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <StarIcon key={i} sx={{ fontSize: 18, color: (theme) => theme.palette.v2.yellow[500] }} />
    ))}
  </Box>
);

const IconBubble = ({ Icon, size = 'lg', tone = 'light' }) => {
  const dims = size === 'lg' ? { box: 56, icon: 24, radius: 2 } : { box: 44, icon: 22, radius: 1.5 };
  const palette = tone === 'dark'
    ? {
      bg: (theme) => theme.palette.v2.surface.glass,
      border: (theme) => `1px solid ${theme.palette.v2.surface.glassBorder}`,
      icon: (theme) => theme.palette.v2.yellow[500]
    }
    : {
      bg: (theme) => theme.palette.v2.yellow[50],
      border: (theme) => `1px solid ${theme.palette.v2.yellow[100]}`,
      icon: (theme) => theme.palette.v2.yellow[800]
    };
  return (
    <Box
      sx={{
        width: dims.box,
        height: dims.box,
        borderRadius: dims.radius,
        backgroundColor: palette.bg,
        border: palette.border,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon sx={{ fontSize: dims.icon, color: palette.icon }} />
    </Box>
  );
};

const LandingPage = () => {
  return (
    <ThemeProviderV2>
      <Helmet>
        <title>AskNeo — Two-Way Texting &amp; Calling CRM for Teams</title>
        <meta
          name="description"
          content="Close more deals with AskNeo's shared inbox for SMS, calls, and contacts. Built for sales, support, and marketing teams."
        />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.v2.surface.dark,
          color: '#FFFFFF'
        }}
      >
        {/* ── Hero (dark) ──────────────────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            pt: { xs: 14, md: 18 },
            pb: { xs: 8, md: 12 },
            backgroundColor: (theme) => theme.palette.v2.surface.dark,
            color: '#FFFFFF'
          }}
        >
          <TopNav variant="dark" ctaTo={signupTo} />

          {/* glow halo behind hero */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              display: { xs: 'none', md: 'block' },
              width: 800,
              height: 600,
              borderRadius: '50%',
              backgroundColor: (theme) => theme.palette.v2.surface.heroGlow,
              opacity: 0.18,
              filter: 'blur(120px)',
              pointerEvents: 'none'
            }}
          />

          <Container sx={{ position: 'relative', textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: (theme) => theme.palette.v2.surface.glass,
                border: (theme) => `1px solid ${theme.palette.v2.surface.glassBorder}`,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: (theme) => theme.palette.v2.yellow[400],
                mb: 3
              }}
            >
              <span role="img" aria-label="trophy">🏆</span>
              #1 Product of the Day on Product Hunt
            </Box>

            <Typography
              variant="h1"
              component="h1"
              sx={{
                maxWidth: 900,
                mx: 'auto',
                fontWeight: 800,
                fontSize: { xs: '2.25rem', sm: '3rem', lg: '3.75rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                color: '#FFFFFF'
              }}
            >
              The two-way texting &amp; calling{' '}
              <Box component="span" sx={{ color: (theme) => theme.palette.v2.yellow[500] }}>
                CRM
              </Box>{' '}
              that closes deals
            </Typography>

            <Typography
              sx={{
                maxWidth: 640,
                mx: 'auto',
                mt: 3,
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: (theme) => theme.palette.v2.surface.mutedText,
                lineHeight: 1.6
              }}
            >
              One shared inbox for texts, calls, and contacts. Your team collaborates in real time to close more sales and keep customers coming back.
            </Typography>

            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PrimaryButton to={signupTo} color="secondary" endIcon={<ArrowForwardIcon />}>
                Start your free trial
              </PrimaryButton>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8125rem', color: (theme) => theme.palette.v2.surface.mutedText }}
              >
                7 Day Free Trial. Cancel anytime
              </Typography>
            </Box>

            {/* hero screenshot */}
            <Box sx={{ mt: 6, maxWidth: 640, mx: 'auto', px: { xs: 0, sm: 2 } }}>
              <Box
                sx={{
                  overflow: 'hidden',
                  borderRadius: 3,
                  border: (theme) => `1px solid ${theme.palette.v2.surface.divider}`,
                  boxShadow: (theme) => (
                    `0 30px 80px ${theme.palette.v2.surface.heroImageShadow}, `
                    + '0 10px 40px rgba(0,0,0,0.6)'
                  )
                }}
              >
                <Box
                  component="img"
                  src={heroImg}
                  alt="AskNeo shared inbox preview"
                  sx={{ display: 'block', width: '100%', height: 'auto' }}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ── Stats (dark band) ───────────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            py: 5,
            backgroundColor: (theme) => theme.palette.v2.surface.band,
            borderBottom: (theme) => `1px solid ${theme.palette.v2.surface.divider}`
          }}
        >
          <Container maxWidth={1000}>
            <Grid container spacing={4}>
              {stats.map((stat) => (
                <Grid item xs={6} md={3} key={stat.label}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: { xs: '1.75rem', md: '2rem' },
                        fontWeight: 800,
                        color: (theme) => theme.palette.v2.yellow[500],
                        lineHeight: 1
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: '0.875rem',
                        color: (theme) => theme.palette.v2.surface.mutedText
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ── Features (dark, alternating rows) ───────────────────────── */}
        <Box component="section" sx={{ py: { xs: 10, md: 14 } }}>
          <Container>
            <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 8, md: 10 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  letterSpacing: '-0.02em',
                  color: '#FFFFFF'
                }}
              >
                Everything you need to{' '}
                <Box component="span" sx={{ color: (theme) => theme.palette.v2.yellow[500] }}>
                  close more deals
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  fontSize: '1rem',
                  color: (theme) => theme.palette.v2.surface.mutedText,
                  maxWidth: 640,
                  mx: 'auto'
                }}
              >
                Purpose-built for sales, support, and marketing teams who want real conversations — not another email tool.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 10, md: 12 } }}>
              {featureRows.map(({ Icon, title, body, image, imageAlt, reverse }) => (
                <Box
                  key={title}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
                    alignItems: 'center',
                    gap: { xs: 5, md: 8 }
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <IconBubble Icon={Icon} size="lg" tone="dark" />
                    <Typography
                      variant="h3"
                      component="h3"
                      sx={{
                        mt: 2.5,
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 700,
                        color: '#FFFFFF'
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.0625rem',
                        lineHeight: 1.6,
                        color: (theme) => theme.palette.v2.surface.mutedText
                      }}
                    >
                      {body}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1.3, width: '100%' }}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        borderRadius: 3,
                        border: (theme) => `1px solid ${theme.palette.v2.surface.divider}`,
                        boxShadow: '0 20px 48px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={imageAlt}
                        loading="lazy"
                        sx={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* supporting feature cards (3-col grid, dark glass) */}
            <Grid container spacing={3} sx={{ mt: { xs: 8, md: 12 } }}>
              {supportingFeatures.map(({ Icon, title, body }) => (
                <Grid item xs={12} md={4} key={title}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 3.5,
                      borderRadius: 3,
                      backgroundColor: (theme) => theme.palette.v2.surface.glass,
                      border: (theme) => `1px solid ${theme.palette.v2.surface.glassBorder}`
                    }}
                  >
                    <IconBubble Icon={Icon} size="sm" tone="dark" />
                    <Typography
                      variant="h4"
                      component="h4"
                      sx={{
                        mt: 2,
                        mb: 1,
                        fontSize: '1.0625rem',
                        fontWeight: 700,
                        color: '#FFFFFF'
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: (theme) => theme.palette.v2.surface.mutedText,
                        lineHeight: 1.6
                      }}
                    >
                      {body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ── Setup steps (dark band) ─────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            py: { xs: 10, md: 14 },
            backgroundColor: (theme) => theme.palette.v2.surface.band,
            borderTop: (theme) => `1px solid ${theme.palette.v2.surface.divider}`,
            borderBottom: (theme) => `1px solid ${theme.palette.v2.surface.divider}`
          }}
        >
          <Container maxWidth={1000}>
            <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 6, md: 8 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  letterSpacing: '-0.02em',
                  color: '#FFFFFF'
                }}
              >
                Up and running in{' '}
                <Box component="span" sx={{ color: (theme) => theme.palette.v2.yellow[500] }}>
                  2 minutes
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  fontSize: '1rem',
                  color: (theme) => theme.palette.v2.surface.mutedText
                }}
              >
                No complex setup. No IT department needed.
              </Typography>
            </Box>
            <Grid container spacing={4}>
              {setupSteps.map(({ number, title, body }) => (
                <Grid item xs={12} md={4} key={number}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 4,
                      textAlign: 'center',
                      borderRadius: 3,
                      backgroundColor: (theme) => theme.palette.v2.surface.glass,
                      border: (theme) => `1px solid ${theme.palette.v2.surface.glassBorder}`
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontSize: { xs: '2.5rem', md: '3rem' },
                        fontWeight: 800,
                        lineHeight: 1,
                        color: (theme) => theme.palette.v2.yellow[500],
                        opacity: 0.45
                      }}
                    >
                      {number}
                    </Typography>
                    <Typography
                      variant="h3"
                      component="h3"
                      sx={{ mt: 2, fontSize: '1.25rem', fontWeight: 700, color: '#FFFFFF' }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '0.9375rem',
                        color: (theme) => theme.palette.v2.surface.mutedText,
                        lineHeight: 1.6
                      }}
                    >
                      {body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ── Testimonials (dark, glass cards) ────────────────────────── */}
        <Box component="section" sx={{ py: { xs: 10, md: 14 } }}>
          <Container maxWidth={1000}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                textAlign: 'center',
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                mb: 6
              }}
            >
              Loved by teams everywhere
            </Typography>
            <Grid container spacing={4}>
              {testimonials.map((t) => (
                <Grid item xs={12} md={6} key={t.authorName}>
                  <Box
                    sx={{
                      height: '100%',
                      p: { xs: 3.5, md: 4 },
                      borderRadius: 3,
                      backgroundColor: (theme) => theme.palette.v2.surface.glass,
                      border: (theme) => `1px solid ${theme.palette.v2.surface.glassBorder}`
                    }}
                  >
                    <Stars />
                    <Typography
                      component="p"
                      sx={{
                        mt: 2.5,
                        fontSize: '1.0625rem',
                        lineHeight: 1.6,
                        color: '#FFFFFF'
                      }}
                    >
                      &ldquo;{t.quote}&rdquo;
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '0.9375rem' }}>
                        {t.authorName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: (theme) => theme.palette.v2.surface.mutedText
                        }}
                      >
                        {t.authorTitle}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ── Final CTA (dark, with yellow glow accent) ───────────────── */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 10, md: 14 },
            backgroundColor: (theme) => theme.palette.v2.surface.dark,
            borderTop: (theme) => `1px solid ${theme.palette.v2.surface.divider}`
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: '-30%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 600,
              height: 600,
              borderRadius: '50%',
              backgroundColor: (theme) => theme.palette.v2.surface.heroGlow,
              opacity: 0.18,
              filter: 'blur(120px)',
              pointerEvents: 'none'
            }}
          />
          <Container maxWidth={700} sx={{ position: 'relative', textAlign: 'center' }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                letterSpacing: '-0.02em',
                color: '#FFFFFF'
              }}
            >
              Ready to close more deals with{' '}
              <Box component="span" sx={{ color: (theme) => theme.palette.v2.yellow[500] }}>
                real conversations
              </Box>
              ?
            </Typography>
            <Typography
              sx={{
                mt: 2,
                fontSize: '1rem',
                maxWidth: 560,
                mx: 'auto',
                color: (theme) => theme.palette.v2.surface.mutedText
              }}
            >
              Join thousands of teams using AskNeo to text, call, and convert customers — all from one shared inbox.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <PrimaryButton to={signupTo} color="secondary" endIcon={<ArrowForwardIcon />}>
                Start your free trial
              </PrimaryButton>
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: '0.8125rem',
                  color: (theme) => theme.palette.v2.surface.mutedText
                }}
              >
                7 Day Free Trial. Cancel anytime
              </Typography>
            </Box>
          </Container>
        </Box>

        <FooterV2 />
      </Box>
    </ThemeProviderV2>
  );
};

export default LandingPage;
